import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { useProductsStore } from '../store/productsStore';
import { formatPrice, Product } from '../data/products';

const SearchModal: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchOpen, setSearchOpen, addToCart } = useStore();
  const products = useProductsStore((state) => state.products);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (query.length >= 2) {
      const q = query.toLowerCase();
      setResults(products.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.categories.some(c => c.toLowerCase().includes(q))
      ));
    } else {
      setResults([]);
    }
  }, [query, products]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [setSearchOpen]);

  if (!searchOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
      <div className="relative max-w-2xl mx-auto mt-20 mx-4 sm:mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-down">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-gray-100">
            <span className="text-[#E31E24] text-xl">🔍</span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('shop.search')}
              className="flex-1 text-lg outline-none text-gray-800 placeholder-gray-400"
            />
            <button
              onClick={() => setSearchOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="max-h-[60vh] overflow-y-auto">
              {results.slice(0, 8).map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0"
                  onClick={() => {
                    setSearchOpen(false);
                    navigate(`/magaza?q=${encodeURIComponent(product.name)}`);
                  }}
                >
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-12 h-12 object-contain rounded-lg bg-gray-50 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-800 truncate">{product.name}</h4>
                    <p className="text-sm font-bold text-[#E31E24]">
                      {product.salePrice > 0 ? `${formatPrice(product.salePrice)} ₺` : product.regularPrice > 0 ? `${formatPrice(product.regularPrice)} ₺` : ''}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                    className="px-3 py-1.5 bg-[#E31E24] text-white text-xs font-bold rounded-lg hover:bg-[#c41a1f] transition-colors shrink-0"
                  >
                    {t('home.addToCart')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {query.length >= 2 && results.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              <p className="text-4xl mb-2">🚗</p>
              <p>{t('shop.noProducts')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
